import {
  ModelMapParameterMapLayerConfiguration,
  ParametersModeledMapLayer as Parameters_ModeledMapLayerConfig,
} from "@/lib/api";
import { DimensionValueSet } from "@/lib/dimensions";
import {
  appendVerticalQueryParam,
  getRelevantDimensionValues,
  dynamicDimensionsContainValues,
} from "@/lib/dimensions/helpers";
import {
  BaseTiledLayer,
  BaseTileImageLoader,
} from "@/lib/map-layers/abstract/base-tiled-layer";
import { GetTileImageProps } from "@/lib/map-layers/abstract/base-tiled-layer/tiled-deck-gl-layer";
import { getAuthToken } from "@/features/shared/features/authentication";
import {
  ModelLayerDataLoader,
  ModelLayerDataLoaderData,
} from "@/features/shared/widgets/model-map/model-layer-data-loader";
import { parametersMapLayersQueue } from "@/lib/map-layers/parameters-map-layers-queue";

import ModelLayerLegendImageLoader from "./model-layer-legend-image-loader";
import ModelLayerLegendImage from "./model-layer-legend-image";

class TileImageLoader extends BaseTileImageLoader {
  private config;

  constructor(config: Parameters_ModeledMapLayerConfig) {
    super();
    this.config = config;
  }

  tileImageRequest(
    dimensionValues: DimensionValueSet,
    { tile, signal }: GetTileImageProps,
  ) {
    const queryString = new URLSearchParams({
      reference_time: dimensionValues["reference_time"]?.queryStringValue,
      time: dimensionValues["time"]?.queryStringValue,
      x: tile.index.x.toString(),
      y: tile.index.y.toString(),
      z: tile.index.z.toString(),
    });
    appendVerticalQueryParam(queryString, dimensionValues);

    return new Request(
      `${import.meta.env.VITE_API_BASE_PATH}/parameters/models/${
        this.config.model_id
      }/map_layers/${this.config.id}/tile?${queryString.toString()}`,
      {
        headers: {
          Authorization: `Bearer ${getAuthToken()?.access_token}`,
        },
        signal,
      },
    );
  }
}

export class Parameters_ModeledMapLayer extends BaseTiledLayer {
  private layerDataLoader: ModelLayerDataLoader;
  private config: Parameters_ModeledMapLayerConfig;
  private widgetConfig: ModelMapParameterMapLayerConfiguration;
  private data?: ModelLayerDataLoaderData;
  private legendImageLoader;

  protected override get queue() {
    return parametersMapLayersQueue;
  }

  constructor(
    modelLayer: Parameters_ModeledMapLayerConfig,
    widgetConfig: ModelMapParameterMapLayerConfiguration,
  ) {
    super({
      tileImageLoader: new TileImageLoader(modelLayer),
    });
    this.config = modelLayer;
    this.widgetConfig = widgetConfig;
    this.layerDataLoader = new ModelLayerDataLoader(modelLayer.model_id, modelLayer.id);
    this.legendImageLoader = new ModelLayerLegendImageLoader(
      modelLayer.model_id,
      modelLayer.id,
    );
  }

  get id() {
    return this.config.id;
  }
  get opacity() {
    return this.config.opacity / 100;
  }
  get tileSize() {
    return this.data?.meta.tile_size ?? 1024;
  }
  get minZoom() {
    return this.data?.meta.zoom_levels.min ?? 4;
  }
  get maxZoom() {
    return this.data?.meta.zoom_levels.max ?? 7;
  }
  get dimensions() {
    return this.data?.dimensions ?? [];
  }

  get extent() {
    const meta = this.data?.meta;
    if (!meta) return undefined;
    return {
      north: meta.geospatial_extent.north_latitude,
      east: meta.geospatial_extent.east_longitude,
      south: meta.geospatial_extent.south_latitude,
      west: meta.geospatial_extent.west_longitude,
    };
  }

  get settings() {
    return [];
  }

  getTitle() {
    return this.widgetConfig.label || this.config.label;
  }
  isForecast() {
    return false;
  }
  isActive(dimensionValues: DimensionValueSet) {
    if (!this.data) return false;
    if (!this.data.modelRun.reference_time) return false;
    if (this.hasLoadError(dimensionValues)) return false;
    if (!this.dimensions) return false;

    return dynamicDimensionsContainValues(this.dimensions, dimensionValues);
  }

  async initialize() {
    this.clearLoadFailures();
    try {
      this.data = await this.layerDataLoader.loadLayerData();
    } catch (error) {
      this.data = undefined;
      throw error;
    }
  }

  getRelevantDimensionValues(values: DimensionValueSet) {
    return getRelevantDimensionValues(this.dimensions, values);
  }

  async loadLegendImage(dimensionValues: DimensionValueSet) {
    return this.legendImageLoader.loadLegendImage(
      this.getRelevantDimensionValues(dimensionValues),
    );
  }

  renderLegend(dimensionValues: DimensionValueSet) {
    const shouldRenderLegend = !!this.data && this.config.has_legend !== false;
    if (!shouldRenderLegend) return undefined;

    const currentDimensionValues = this.getRelevantDimensionValues(dimensionValues);

    return (
      <ModelLayerLegendImage
        layerId={this.config.id}
        opacity={this.opacity}
        loadLegendImage={this.loadLegendImage.bind(this)}
        dimensionValues={currentDimensionValues}
      />
    );
  }

  renderLayer(currentDimensionValues: DimensionValueSet) {
    if (!this.isActive(currentDimensionValues)) return;

    return super.renderLayer(currentDimensionValues);
  }
}
